import * as React from "react";
import { Filter } from "lucide-react";

import { cn, formatStatusLabel } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Per-column filters: a funnel icon in a DataTable header opens a popover
// with that column's filter fields. Fields edit a local draft; nothing
// reaches the URL (and the query) until Apply — Clear removes just this
// column's filters. The icon is highlighted while any of them is set.

export interface DataTableColumnFilter {
  /** Highlights the header icon: one of this column's filters is set. */
  active: boolean;
  /** The popover body — typically a `ColumnFilterForm`. Call `close()` once applied. */
  render: (close: () => void) => React.ReactNode;
}

export const FILTER_FIELD_CLASSNAME = cn(
  "h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm",
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
);

/** A labelled field inside a filter popover. */
export function FilterField({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

export interface FilterOption {
  value: string;
  label: string;
}

/** An enum's values as options, labelled the way status badges are. */
export function enumOptions(values: readonly string[]): FilterOption[] {
  return values.map((value) => ({ value, label: formatStatusLabel(value) }));
}

/** A select whose "all" choice means unset (`undefined`). */
export function SelectFilterField({
  id,
  label,
  allLabel,
  options,
  value,
  onChange,
}: {
  id: string;
  label: string;
  allLabel: string;
  options: FilterOption[];
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  return (
    <FilterField id={id} label={label}>
      <select
        id={id}
        className={FILTER_FIELD_CLASSNAME}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FilterField>
  );
}

/** A free-text field (blank means unset — `ColumnFilterForm` drops it on Apply). */
export function TextFilterField({
  id,
  label,
  placeholder,
  value,
  onChange,
}: {
  id: string;
  label: string;
  placeholder?: string;
  value: string | undefined;
  onChange: (value: string) => void;
}) {
  return (
    <FilterField id={id} label={label}>
      <input
        id={id}
        placeholder={placeholder}
        className={FILTER_FIELD_CLASSNAME}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </FilterField>
  );
}

/** Min/max inputs side by side — amounts stay decimal strings. */
export function RangeFilterFields({
  idPrefix,
  minLabel = "Min",
  maxLabel = "Max",
  min,
  max,
  onMinChange,
  onMaxChange,
}: {
  idPrefix: string;
  minLabel?: string;
  maxLabel?: string;
  min: string | undefined;
  max: string | undefined;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <FilterField id={`${idPrefix}-min`} label={minLabel}>
        <input
          id={`${idPrefix}-min`}
          inputMode="decimal"
          placeholder="No minimum"
          className={FILTER_FIELD_CLASSNAME}
          value={min ?? ""}
          onChange={(e) => onMinChange(e.target.value)}
        />
      </FilterField>
      <FilterField id={`${idPrefix}-max`} label={maxLabel}>
        <input
          id={`${idPrefix}-max`}
          inputMode="decimal"
          placeholder="No maximum"
          className={FILTER_FIELD_CLASSNAME}
          value={max ?? ""}
          onChange={(e) => onMaxChange(e.target.value)}
        />
      </FilterField>
    </div>
  );
}

/** From/to date inputs side by side (`YYYY-MM-DD`). */
export function DateRangeFilterFields({
  idPrefix,
  fromLabel = "From",
  toLabel = "To",
  from,
  to,
  onFromChange,
  onToChange,
}: {
  idPrefix: string;
  fromLabel?: string;
  toLabel?: string;
  from: string | undefined;
  to: string | undefined;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <FilterField id={`${idPrefix}-from`} label={fromLabel}>
        <input
          id={`${idPrefix}-from`}
          type="date"
          className={FILTER_FIELD_CLASSNAME}
          value={from ?? ""}
          onChange={(e) => onFromChange(e.target.value)}
        />
      </FilterField>
      <FilterField id={`${idPrefix}-to`} label={toLabel}>
        <input
          id={`${idPrefix}-to`}
          type="date"
          className={FILTER_FIELD_CLASSNAME}
          value={to ?? ""}
          onChange={(e) => onToChange(e.target.value)}
        />
      </FilterField>
    </div>
  );
}

export function ColumnFilterButton({ header, filter }: { header: string; filter: DataTableColumnFilter }) {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Filter by ${header}${filter.active ? " (active)" : ""}`}
          className={cn(
            "relative inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted",
            filter.active ? "text-primary" : "text-muted-foreground/60 hover:text-foreground",
          )}
        >
          <Filter className={cn("h-3.5 w-3.5", filter.active && "fill-current")} />
          {filter.active && <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-primary" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3">
        {filter.render(() => setOpen(false))}
      </PopoverContent>
    </Popover>
  );
}

type Setter<D> = <K extends keyof D>(key: K, value: D[K]) => void;

export interface ColumnFilterFormProps<D extends Record<string, unknown>> {
  /** The currently applied values of this column's filter keys — the draft starts from them. */
  initial: D;
  /** Receives every key of `initial`: the draft on Apply (blank strings as `undefined`), all `undefined` on Clear. */
  onApply: (values: D) => void;
  close: () => void;
  children: (draft: D, set: Setter<D>) => React.ReactNode;
}

export function ColumnFilterForm<D extends Record<string, unknown>>({
  initial,
  onApply,
  close,
  children,
}: ColumnFilterFormProps<D>) {
  const [draft, setDraft] = React.useState<D>(initial);
  const set: Setter<D> = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const hasApplied = Object.values(initial).some((v) => v !== undefined);

  function apply(values: D) {
    const normalized = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, v === "" ? undefined : v]),
    ) as D;
    onApply(normalized);
    close();
  }

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        apply(draft);
      }}
    >
      {children(draft, set)}
      <div className="flex items-center justify-between gap-2 pt-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={!hasApplied}
          onClick={() => apply(Object.fromEntries(Object.keys(initial).map((k) => [k, undefined])) as D)}
        >
          Clear
        </Button>
        <Button type="submit" size="sm">
          Apply
        </Button>
      </div>
    </form>
  );
}

/**
 * A column's `filter` wired to a `useTableState` page: `keys` are the filter
 * keys this column owns, `fields` renders their inputs against the draft.
 */
export function columnFilter<F extends Record<string, unknown>, K extends keyof F & string>(
  state: { filters: F; setFilters: (patch: Partial<F>) => void },
  keys: readonly K[],
  fields: (draft: Pick<F, K>, set: Setter<Pick<F, K>>) => React.ReactNode,
): DataTableColumnFilter {
  const initial = Object.fromEntries(keys.map((k) => [k, state.filters[k]])) as Pick<F, K>;
  return {
    active: keys.some((k) => state.filters[k] !== undefined),
    render: (close) => (
      <ColumnFilterForm initial={initial} onApply={(values) => state.setFilters(values as Partial<F>)} close={close}>
        {fields}
      </ColumnFilterForm>
    ),
  };
}
